import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import AppLayout from "@/components/layout/AppLayout";
import AuthLayout from "@/components/layout/AuthLayout";
import FullLayout from "@/components/layout/FullLayout";
import { ROUTES } from "@/constants/routes";
import PrivateRoute from "@/router/PrivateRoute";
import { routeFor } from "@/routes";

import BarangaysPage from "@/pages/BarangaysPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import CitiesPage from "@/pages/CitiesPage";
import ComingSoonPage from "@/pages/ComingSoonPage";
import ConfigurationPage from "@/pages/ConfigurationPage";
import DashboardPage from "@/pages/DashboardPage";
import EmployeeActivityLogPage from "@/pages/EmployeeActivityLogPage";
import EmployeesPage from "@/pages/EmployeesPage";
import LoginPage from "@/pages/LoginPage";
import MemberActivityLogPage from "@/pages/MemberActivityLogPage";
import MemberAnalyticsPage from "@/pages/MemberAnalyticsPage";
import MemberDetailPage from "@/pages/MemberDetailPage";
import MemberNewPage from "@/pages/MemberNewPage";
import MemberPopulationPage from "@/pages/MemberPopulationPage";
import MembersPage from "@/pages/MembersPage";
import NonMembersPage from "@/pages/NonMembersPage";
import ProfilePage from "@/pages/ProfilePage";
import ProfilePasswordPage from "@/pages/ProfilePasswordPage";
import ProvincesPage from "@/pages/ProvincesPage";
import ResetPasswordRequestPage from "@/pages/ResetPasswordRequestPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import YellowPageCategoriesPage from "@/pages/YellowPageCategoriesPage";
import YellowPagePage from "@/pages/YellowPagePage";

/**
 * Renders our own BrowserRouter only when asked to. When the host micro-
 * frontend shell already provides a Router, it passes isBrowserRouter and
 * we mount a fragment so useLocation resolves against the host router.
 */
const BrowserWrapper = ({
  isDisabledBrowserRouter,
  children,
}: {
  isDisabledBrowserRouter?: boolean | undefined;
  children: ReactNode;
}) => {
  if (!isDisabledBrowserRouter) return <BrowserRouter>{children}</BrowserRouter>;
  return <>{children}</>;
};

/** The catalog's stable uuid for a path, as the route `handle` the shell reads. */
const handle = (path: string) => ({ code: routeFor(path)?.code });

const AppRouter = (props: { isBrowserRouter?: boolean | undefined }) => (
  <BrowserWrapper isDisabledBrowserRouter={props.isBrowserRouter}>
    <Routes>
      {/* Signed-out. The layout itself bounces anyone who already has a session. */}
      <Route element={<AuthLayout />}>
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.RESET_PASSWORD_REQUEST} element={<ResetPasswordRequestPage />} />
        <Route path={ROUTES.SET_PASSWORD} element={<SetPasswordPage />} />
      </Route>

      <Route element={<PrivateRoute />}>
        {/* Whole-screen signed-in pages: no rail, no header. */}
        <Route element={<FullLayout />}>
          <Route path={ROUTES.CHANGE_PASSWORD} element={<ChangePasswordPage />} />
          <Route path={ROUTES.MEMBER_NEW} element={<MemberNewPage />} />
        </Route>

        {/* Everything inside the shell. */}
        <Route element={<AppLayout />}>
          <Route index element={<DashboardPage />} handle={handle(ROUTES.DASHBOARD)} />
          <Route path={ROUTES.MEMBERS} element={<MembersPage />} handle={handle(ROUTES.MEMBERS)} />
          <Route
            path={ROUTES.MEMBER_DETAIL}
            element={<MemberDetailPage />}
            handle={handle(ROUTES.MEMBER_DETAIL)}
          />
          <Route
            path={ROUTES.MEMBER_ANALYTICS}
            element={<MemberAnalyticsPage />}
            handle={handle(ROUTES.MEMBER_ANALYTICS)}
          />
          <Route
            path={ROUTES.MEMBER_ACTIVITY_LOG}
            element={<MemberActivityLogPage />}
            handle={handle(ROUTES.MEMBER_ACTIVITY_LOG)}
          />
          <Route
            path={ROUTES.MEMBER_POPULATION}
            element={<MemberPopulationPage />}
            handle={handle(ROUTES.MEMBER_POPULATION)}
          />
          <Route
            path={ROUTES.NON_MEMBERS}
            element={<NonMembersPage />}
            handle={handle(ROUTES.NON_MEMBERS)}
          />
          <Route
            path={ROUTES.EMPLOYEES}
            element={<EmployeesPage />}
            handle={handle(ROUTES.EMPLOYEES)}
          />
          <Route
            path={ROUTES.EMPLOYEE_ACTIVITY_LOG}
            element={<EmployeeActivityLogPage />}
            handle={handle(ROUTES.EMPLOYEE_ACTIVITY_LOG)}
          />
          <Route
            path={ROUTES.YELLOW_PAGE}
            element={<YellowPagePage />}
            handle={handle(ROUTES.YELLOW_PAGE)}
          />
          <Route
            path={ROUTES.YELLOW_PAGE_CATEGORIES}
            element={<YellowPageCategoriesPage />}
            handle={handle(ROUTES.YELLOW_PAGE_CATEGORIES)}
          />
          <Route
            path={ROUTES.PROVINCES}
            element={<ProvincesPage />}
            handle={handle(ROUTES.PROVINCES)}
          />
          <Route path={ROUTES.CITIES} element={<CitiesPage />} handle={handle(ROUTES.CITIES)} />
          <Route
            path={ROUTES.BARANGAYS}
            element={<BarangaysPage />}
            handle={handle(ROUTES.BARANGAYS)}
          />
          <Route
            path={ROUTES.CONFIGURATION}
            element={<ConfigurationPage />}
            handle={handle(ROUTES.CONFIGURATION)}
          />
          <Route path={ROUTES.PROFILE} element={<ProfilePage />} handle={handle(ROUTES.PROFILE)} />
          <Route
            path={ROUTES.PROFILE_PASSWORD}
            element={<ProfilePasswordPage />}
            handle={handle(ROUTES.PROFILE_PASSWORD)}
          />
          {/* Menu entries with no page yet — one slug each, 404 for anything else. */}
          <Route path={ROUTES.COMING_SOON} element={<ComingSoonPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
  </BrowserWrapper>
);

export default AppRouter;
